import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Button } from "../ui/button";
import { Loader2, Sparkles, TrendingUp, PiggyBank, AlertTriangle } from "lucide-react";
import ReactMarkdown from "react-markdown";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend, ReferenceLine,
} from "recharts";

const eur = v => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const eurK = v => `${Math.round((v || 0) / 1000)}k`;

const CR_LINES = [
  ["Chiffre d'affaires", "ca"], ["Charges variables", "charges_variables"],
  ["Charges fixes", "charges_fixes"], ["Masse salariale", "masse_salariale"],
  ["EBE", "ebe", true], ["Dotations amortissements", "dotations"],
  ["Charges financières", "interets"], ["Résultat avant impôt", "resultat_avant_impot"],
  ["Impôt sur les sociétés", "impot_societes"], ["Résultat net", "resultat_net", true], ["CAF", "caf"],
];

const FIN_LINES = [
  ["Besoins — Investissements", "investissements"], ["Besoins — Remb. capital emprunt", "remboursement_capital"],
  ["Total besoins", "total_besoins", true], ["Ressources — Apport", "apport"],
  ["Ressources — Emprunt", "emprunt"], ["Ressources — CAF", "caf"],
  ["Total ressources", "total_ressources", true], ["Solde annuel", "solde"], ["Solde cumulé", "solde_cumule", true],
];

function YearTable({ lines, rows, testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-white/10 text-slate-500 uppercase text-[10px]">
            <th className="text-left py-2 px-2"></th>
            {rows.map(r => <th key={r.annee} className="text-right py-2 px-2">Année {r.annee}</th>)}
          </tr>
        </thead>
        <tbody>
          {lines.map(([label, key, bold]) => (
            <tr key={key} className={`border-b border-white/5 ${bold ? "bg-white/[0.03]" : ""}`}>
              <td className={`py-1.5 px-2 ${bold ? "font-bold text-white" : "text-slate-300"}`}>{label}</td>
              {rows.map(r => (
                <td key={r.annee} className={`py-1.5 px-2 text-right tabular-nums ${bold ? "font-bold" : ""} ${r[key] < 0 ? "text-rose-400" : "text-slate-200"}`}>
                  {eur(r[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ResultatsView({ computed, aiNote, onAnalyze, analyzing }) {
  if (!computed) return null;
  const { annees, mensuel, tresorerie, financement } = computed;
  const rn3 = annees[2]?.resultat_net || 0;
  const lowNeg = (tresorerie.point_bas?.solde || 0) < 0;

  const chartTreso = mensuel.map(m => ({ p: m.period, solde: m.solde }));
  const chartCr = annees.map(a => ({ name: `An ${a.annee}`, CA: a.ca, "Résultat net": a.resultat_net }));

  return (
    <div className="space-y-4" data-testid="resultats-view">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi label="CA Année 3" value={eur(annees[2]?.ca)} icon={TrendingUp} color="#10b981" testId="kpi-ca-an3" />
        <Kpi label="Résultat net An 3" value={eur(rn3)} icon={TrendingUp} color={rn3 >= 0 ? "#f7c935" : "#ef4444"} testId="kpi-rn-an3" />
        <Kpi label="Point bas trésorerie" value={eur(tresorerie.point_bas?.solde)} sub={tresorerie.point_bas?.period}
          icon={lowNeg ? AlertTriangle : PiggyBank} color={lowNeg ? "#ef4444" : "#3b82f6"} testId="kpi-point-bas" />
        <Kpi label="Trésorerie fin An 3" value={eur(tresorerie.fin_an3)} icon={PiggyBank}
          color={tresorerie.fin_an3 >= 0 ? "#10b981" : "#ef4444"} testId="kpi-treso-an3" />
      </div>

      <Tabs defaultValue="resultat">
        <TabsList className="bg-[#14101c] border border-white/5 flex-wrap h-auto">
          <TabsTrigger value="resultat" data-testid="tab-resultat">Compte de résultat</TabsTrigger>
          <TabsTrigger value="tresorerie" data-testid="tab-tresorerie">Trésorerie</TabsTrigger>
          <TabsTrigger value="financement" data-testid="tab-financement">Plan de financement</TabsTrigger>
          <TabsTrigger value="synthese" data-testid="tab-synthese">Note de synthèse IA</TabsTrigger>
        </TabsList>

        <TabsContent value="resultat">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-4">
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartCr}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <YAxis tickFormatter={eurK} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <Tooltip formatter={v => eur(v)} contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="CA" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Résultat net" fill="#f7c935" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <YearTable lines={CR_LINES} rows={annees} testId="table-compte-resultat" />
          </div>
        </TabsContent>

        <TabsContent value="tresorerie">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 space-y-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartTreso}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="p" tick={{ fill: "#94a3b8", fontSize: 9 }} interval={2} />
                  <YAxis tickFormatter={eurK} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <Tooltip formatter={v => eur(v)} contentStyle={{ background: "#0b0811", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                  <ReferenceLine y={0} stroke="#ef4444" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="solde" name="Solde de trésorerie" stroke="#3b82f6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="overflow-x-auto" data-testid="table-tresorerie-an1">
              <p className="text-[10px] uppercase text-slate-500 font-bold mb-2">Année 1 — détail mensuel</p>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-white/10 text-slate-500 uppercase text-[10px]">
                    <th className="text-left py-1.5 px-2">Mois</th>
                    <th className="text-right py-1.5 px-2">Encaissements</th>
                    <th className="text-right py-1.5 px-2">Décaissements</th>
                    <th className="text-right py-1.5 px-2">Solde</th>
                  </tr>
                </thead>
                <tbody>
                  {mensuel.slice(0, 12).map(m => (
                    <tr key={m.mois} className="border-b border-white/5">
                      <td className="py-1 px-2 text-slate-300">{m.period}</td>
                      <td className="py-1 px-2 text-right tabular-nums text-emerald-400">{eur(m.encaissements)}</td>
                      <td className="py-1 px-2 text-right tabular-nums text-rose-400">{eur(m.decaissements + m.is_paye)}</td>
                      <td className={`py-1 px-2 text-right tabular-nums font-bold ${m.solde < 0 ? "text-rose-400" : "text-white"}`}>{eur(m.solde)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="financement">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
            <YearTable lines={FIN_LINES} rows={financement} testId="table-financement" />
          </div>
        </TabsContent>

        <TabsContent value="synthese">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid="synthese-panel">
            <div className="flex items-center gap-2 mb-3">
              <p className="text-sm font-bold text-white flex-1">Note de synthèse pour le dossier bancaire</p>
              <Button size="sm" onClick={onAnalyze} disabled={analyzing}
                className="bg-gradient-to-r from-violet-600 to-blue-600 text-white text-xs font-bold" data-testid="generate-synthese-btn">
                {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> {aiNote ? "Régénérer" : "Générer avec l'IA"}</>}
              </Button>
            </div>
            {aiNote ? (
              <div className="prose prose-invert prose-sm max-w-none text-slate-300 text-sm [&_h2]:text-[#f7c935] [&_h2]:text-sm [&_h2]:font-bold [&_h2]:mt-4 [&_strong]:text-white" data-testid="synthese-content">
                <ReactMarkdown>{aiNote}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-xs text-slate-500 py-6 text-center">Générez la note de synthèse : elle sera intégrée à la fin du dossier PDF exporté.</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, color, testId }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-3.5" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
        <Icon className="w-3 h-3" style={{ color }} /> {label}
      </div>
      <div className="text-lg font-black tabular-nums" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </div>
  );
}
